import { HttpErrorResponse } from '@angular/common/http';
import {
    IMultiYearAvailableLanguage,
    IMultiYearComparisonResponseDTO,
} from '@modeso/types__aumico-lib-annualreport-be';
import { IMycCompanyPreferences } from '@modeso/types__aumico-lib-companies-be';
import { createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';
import * as fromActions from '../actions/analytics.actions';
import {
    ControlsState,
    DEFAULT_DELTA_MODE,
    DEFAULT_REPORT_SCOPE,
    MAX_SELECTED_YEARS,
    MIN_SELECTED_YEARS,
} from '../components/multi-year-comparison/models/controls.model';
import { collectExpandableNodeIds, collectGroupNodeIds } from '../components/multi-year-comparison/models/tree-walk.util';

export const ANALYTICS_FEATURE_KEY = 'aumicoCompaniesAnalytics';

export interface AnalyticsCompanyEntry {
    loading: boolean;
    harmonized: IMultiYearComparisonResponseDTO | null;
    error: HttpErrorResponse | null;
    controls: ControlsState;
    /** True between `requestMultiYearPdf` dispatch and the success / failure result. */
    exportInFlight: boolean;
    /**
     * Set when the BE responded with HTTP 409 `LANGUAGE_MISMATCH`. The
     * container subscribes to this and opens the language-selection modal
     * the first time it becomes non-null. Cleared once the user picks a
     * language (via `setMultiYearLanguage`) or dismisses the modal.
     */
    pendingLanguageSelection: IMultiYearAvailableLanguage[] | null;
    /**
     * Locale the user explicitly chose for the MYC. Hydrated from
     * `Company.mycPreferences.selectedLanguage` and updated via
     * `setMultiYearLanguage`. Sent as `?language=` on subsequent fetches.
     * `null` when the user has not pinned one yet.
     */
    selectedLanguage: string | null;
    /**
     * Last-confirmed export-modal preferences for this company. Used as the
     * initial state when the modal opens next time. Hydrated from
     * `Company.mycPreferences` on company load; updated by
     * `persistExportModalConfirm`. `selectedNodeIds` may contain stale ids
     * — the modal filters them against the live tree at open-time.
     */
    exportPreferences: {
        includeBalance: boolean;
        includeIncome: boolean;
        showAccounts: boolean;
        showRatios: boolean;
        selectedNodeIds: string[];
    } | null;
    /**
     * Dirty marker for the controls-bar persisted fields (selectedYears,
     * deltaMode, structMode, selectedLanguage). Set `true` by every action
     * that mutates one of those fields; cleared by `mycPreferencesFlushed`
     * after a successful PATCH (and by `hydrateMycPreferences` because the
     * server's state IS the new truth).
     *
     * The `periodicFlush$` effect ticks every 30s and dispatches a flush
     * for every entry with `dirty = true`. The container additionally
     * dispatches flush on `ngOnDestroy` and on `companyId` switch so
     * page-leave doesn't lose recent changes.
     */
    mycPreferencesDirty: boolean;
}

export interface AnalyticsState {
    byCompany: Record<string, AnalyticsCompanyEntry>;
}

const INITIAL_CONTROLS: ControlsState = {
    selectedYears: [],
    deltaMode: DEFAULT_DELTA_MODE,
    reportScope: DEFAULT_REPORT_SCOPE,
    structMode: false,
    expandedNodes: [],
};

export const initialState: AnalyticsState = {
    byCompany: {},
};

function emptyEntry(): AnalyticsCompanyEntry {
    return {
        loading: false,
        harmonized: null,
        error: null,
        controls: { ...INITIAL_CONTROLS },
        exportInFlight: false,
        pendingLanguageSelection: null,
        selectedLanguage: null,
        exportPreferences: null,
        mycPreferencesDirty: false,
    };
}

function withEntry(
    state: AnalyticsState,
    companyId: string,
    update: (entry: AnalyticsCompanyEntry) => AnalyticsCompanyEntry,
): AnalyticsState {
    const existing = state.byCompany[companyId] ?? emptyEntry();
    return {
        ...state,
        byCompany: { ...state.byCompany, [companyId]: update(existing) },
    };
}

function withControls(
    state: AnalyticsState,
    companyId: string,
    update: (controls: ControlsState) => ControlsState,
): AnalyticsState {
    return withEntry(state, companyId, (entry) => ({
        ...entry,
        controls: update(entry.controls),
    }));
}

/**
 * Same as `withControls` but also flips `mycPreferencesDirty: true`. Used by
 * the reducer cases for persisted-field mutations (`toggleYear`,
 * `setDeltaMode`, `toggleStructMode`). The periodic / page-leave flush
 * picks the dirty flag up and PATCHes the BE.
 */
function withControlsDirty(
    state: AnalyticsState,
    companyId: string,
    update: (controls: ControlsState) => ControlsState,
): AnalyticsState {
    return withEntry(state, companyId, (entry) => ({
        ...entry,
        controls: update(entry.controls),
        mycPreferencesDirty: true,
    }));
}

/** Pick the (up to) 6 most recent years as the default selection. */
function pickDefaultYears(allYears: string[]): string[] {
    return allYears.slice(0, MAX_SELECTED_YEARS);
}

/**
 * Reconcile a previous year selection against a fresh `yearLabels` list (e.g.
 * after a language switch where the new locale's reports yield different
 * date strings). Keeps survivors in order, then pads with newest-first from
 * `allYears` until the result is at least `MIN_SELECTED_YEARS` long.
 * Capped at `MAX_SELECTED_YEARS` for safety.
 */
function reconcileSelectedYears(prevSelected: string[], allYears: string[]): string[] {
    const survivors = prevSelected.filter((y) => allYears.includes(y));
    if (survivors.length >= MIN_SELECTED_YEARS) {
        return survivors.slice(0, MAX_SELECTED_YEARS);
    }
    // Pad with the newest years not already in the survivor list, in order.
    const result = [...survivors];
    for (const y of allYears) {
        if (result.length >= MIN_SELECTED_YEARS) break;
        if (!result.includes(y)) result.push(y);
    }
    return result.slice(0, MAX_SELECTED_YEARS);
}

/**
 * Walk the harmonized tree and collect every node id (including ACCOUNT
 * nodes). Used to filter stored `selectedNodeIds` against the live tree at
 * `loadAnalyticsSuccess` time — handles the "new master report changed the
 * tree shape" case where stored ids no longer correspond to a real node.
 */
function collectAllNodeIds(harmonized: IMultiYearComparisonResponseDTO): string[] {
    const ids: string[] = [];
    const walk = (node: { id: string; children: any[] } | null): void => {
        if (node == null) return;
        ids.push(node.id);
        for (const c of node.children) walk(c);
    };
    walk(harmonized.assets as any);
    walk(harmonized.liabilities as any);
    walk(harmonized.incomeStatement as any);
    return ids;
}

export const analyticsReducer = createReducer(
    initialState,
    on(fromActions.loadAnalyticsForCompany, (state, { companyId }) =>
        withEntry(state, companyId, (entry) => ({
            ...entry,
            loading: true,
            error: null,
        })),
    ),
    on(fromActions.loadAnalyticsSuccess, (state, { companyId, harmonized }) =>
        withEntry(state, companyId, (entry) => {
            const isFirstLoad = entry.controls.selectedYears.length === 0;
            // Reconcile the persisted export selection against the freshly-
            // loaded tree — a new master report can rename/remove nodes,
            // leaving stored ids stale. Drop the dead ones silently.
            let exportPreferences = entry.exportPreferences;
            if (exportPreferences != null && exportPreferences.selectedNodeIds.length > 0) {
                const liveIds = new Set(collectAllNodeIds(harmonized));
                const survivors = exportPreferences.selectedNodeIds.filter((id) => liveIds.has(id));
                if (survivors.length !== exportPreferences.selectedNodeIds.length) {
                    exportPreferences = { ...exportPreferences, selectedNodeIds: survivors };
                }
            }
            return {
                ...entry,
                loading: false,
                error: null,
                harmonized,
                exportPreferences,
                controls: {
                    ...entry.controls,
                    // First load → take the newest N years. Subsequent loads
                    // (e.g. language switch — different reports, different
                    // year labels) → reconcile the previous selection against
                    // the new labels and pad up to `MIN_SELECTED_YEARS` so
                    // the table never renders with too few columns to compute
                    // a Δ (which needs newest vs. oldest).
                    selectedYears: isFirstLoad
                        ? pickDefaultYears(harmonized.yearLabels)
                        : reconcileSelectedYears(entry.controls.selectedYears, harmonized.yearLabels),
                    // On first load open all GROUP nodes; FLIs start collapsed.
                    expandedNodes: isFirstLoad
                        ? collectGroupNodeIds(harmonized)
                        : entry.controls.expandedNodes,
                },
            };
        }),
    ),
    on(fromActions.loadAnalyticsFailure, (state, { companyId, error }) =>
        withEntry(state, companyId, (entry) => ({
            ...entry,
            loading: false,
            error,
        })),
    ),
    // Year toggle, delta-mode change and struct-mode toggle all flip a
    // persisted setting → mark the entry dirty so the periodic flush /
    // page-leave flush picks it up. `reportScope` is intentionally NOT
    // persisted (always reset to Bilanz on load) so it does NOT set dirty.
    on(fromActions.toggleYear, (state, { companyId, yearLabel }) =>
        withControlsDirty(state, companyId, (controls) => {
            const isSelected = controls.selectedYears.includes(yearLabel);
            if (isSelected) {
                if (controls.selectedYears.length <= MIN_SELECTED_YEARS) {
                    // At the minimum — reject the toggle and keep the current selection.
                    return controls;
                }
                return {
                    ...controls,
                    selectedYears: controls.selectedYears.filter((y) => y !== yearLabel),
                };
            }
            if (controls.selectedYears.length >= MAX_SELECTED_YEARS) return controls;
            // Keep the order matching harmonized.yearLabels (newest → oldest).
            const harmonized = state.byCompany[companyId]?.harmonized;
            const allYears = harmonized?.yearLabels ?? [];
            const updated = new Set([...controls.selectedYears, yearLabel]);
            return {
                ...controls,
                selectedYears: allYears.filter((y) => updated.has(y)),
            };
        }),
    ),
    on(fromActions.setDeltaMode, (state, { companyId, mode }) =>
        withControlsDirty(state, companyId, (controls) => ({ ...controls, deltaMode: mode })),
    ),
    on(fromActions.setReportScope, (state, { companyId, scope }) =>
        withControls(state, companyId, (controls) => ({ ...controls, reportScope: scope })),
    ),
    on(fromActions.toggleStructMode, (state, { companyId }) =>
        withControlsDirty(state, companyId, (controls) => ({ ...controls, structMode: !controls.structMode })),
    ),
    on(fromActions.toggleNode, (state, { companyId, nodeId }) =>
        withControls(state, companyId, (controls) => {
            const set = new Set(controls.expandedNodes);
            if (set.has(nodeId)) {
                set.delete(nodeId);
            } else {
                set.add(nodeId);
            }
            return { ...controls, expandedNodes: Array.from(set) };
        }),
    ),
    on(fromActions.expandAllNodes, (state, { companyId }) => {
        const harmonized = state.byCompany[companyId]?.harmonized;
        if (!harmonized) return state;
        return withControls(state, companyId, (controls) => ({
            ...controls,
            expandedNodes: collectExpandableNodeIds(harmonized),
        }));
    }),
    on(fromActions.collapseAllNodes, (state, { companyId }) => {
        const harmonized = state.byCompany[companyId]?.harmonized;
        if (!harmonized) return state;
        const groupIdSet = new Set(collectGroupNodeIds(harmonized));
        return withControls(state, companyId, (controls) => ({
            ...controls,
            // Keep GROUP nodes at their current expanded state; only collapse FLIs.
            expandedNodes: controls.expandedNodes.filter((id) => groupIdSet.has(id)),
        }));
    }),
    // Language selection — set from the effect when the BE returned a 409
    // mismatch. Container reads it to decide when to open the modal.
    on(fromActions.requireLanguageSelection, (state, { companyId, availableLanguages }) =>
        withEntry(state, companyId, (entry) => ({
            ...entry,
            loading: false,
            // Block the loading spinner while we wait for the user's pick.
            pendingLanguageSelection: availableLanguages,
        })),
    ),
    // User picked a language (via the modal or the controls-bar dropdown).
    // Clear the pending flag and mark loading — the effect re-fetches with
    // the chosen `?language=` carried in the action props. We deliberately
    // DON'T persist the pick in state: a fresh load (`loadAnalyticsForCompany`)
    // always fetches WITHOUT a language pin so the BE can re-evaluate from
    // scratch. The BE response's `activeLanguage` drives the dropdown display.
    on(fromActions.setMultiYearLanguage, (state, { companyId, language }) =>
        withEntry(state, companyId, (entry) => ({
            ...entry,
            // Persist the pick in store so the next `loadAnalyticsForCompany`
            // also sends it as `?language=`. The periodic / page-leave flush
            // writes it to `Company.mycPreferences` so the choice survives
            // a page reload.
            selectedLanguage: language,
            pendingLanguageSelection: null,
            loading: true,
            error: null,
            mycPreferencesDirty: true,
        })),
    ),
    // PDF export progress flag — drives the disabled state on the export button.
    on(fromActions.requestMultiYearPdf, (state, { companyId }) =>
        withEntry(state, companyId, (entry) => ({ ...entry, exportInFlight: true })),
    ),
    on(fromActions.requestMultiYearPdfSuccess, (state, { companyId }) =>
        withEntry(state, companyId, (entry) => ({ ...entry, exportInFlight: false })),
    ),
    on(fromActions.requestMultiYearPdfFailure, (state, { companyId }) =>
        withEntry(state, companyId, (entry) => ({ ...entry, exportInFlight: false })),
    ),
    // ─── Persisted MYC preferences ─────────────────────────────────────────
    // `hydrateMycPreferences` runs once per page-visit, right after the
    // company is loaded. Merges the persisted Company.mycPreferences into
    // the analytics entry. `loadAnalyticsSuccess` then takes over for the
    // post-load reconciliation against the actual `harmonized` data.
    on(fromActions.hydrateMycPreferences, (state, { companyId, preferences }) =>
        withEntry(state, companyId, (entry) => ({
            ...entry,
            // Server-state IS the new truth post-hydration. Clear dirty so
            // we don't immediately re-PATCH the same values we just read.
            mycPreferencesDirty: false,
            selectedLanguage: preferences.selectedLanguage ?? entry.selectedLanguage,
            controls: {
                ...entry.controls,
                // Hydrate the controls-bar fields that we persist. The
                // `loadAnalyticsSuccess` reducer reconciles `selectedYears`
                // against `harmonized.yearLabels` separately.
                selectedYears: preferences.selectedYears ?? entry.controls.selectedYears,
                deltaMode: preferences.deltaMode ?? entry.controls.deltaMode,
                structMode: preferences.structMode ?? entry.controls.structMode,
            },
            // Snapshot the export-modal defaults. Stale `selectedNodeIds`
            // (against the live tree) are filtered later in
            // `loadAnalyticsSuccess` — at hydration time we may not even
            // have the harmonized tree yet.
            exportPreferences: hasExportPrefs(preferences)
                ? {
                    includeBalance: preferences.includeBalance ?? true,
                    includeIncome: preferences.includeIncome ?? true,
                    showAccounts: preferences.showAccounts ?? false,
                    showRatios: preferences.showRatios ?? false,
                    selectedNodeIds: preferences.selectedNodeIds ?? [],
                }
                : entry.exportPreferences,
        })),
    ),
    // Final export-modal selection captured at Confirm time. The persist
    // effect listens to this and PATCHes the BE.
    on(fromActions.persistExportModalConfirm, (state, { companyId, includeBalance, includeIncome, showAccounts, showRatios, selectedNodeIds }) =>
        withEntry(state, companyId, (entry) => ({
            ...entry,
            exportPreferences: {
                includeBalance,
                includeIncome,
                showAccounts,
                showRatios,
                selectedNodeIds,
            },
        })),
    ),
    // Confirmation from the flush effect after a successful PATCH. Clears
    // dirty so the next periodic tick doesn't re-write the same values.
    on(fromActions.mycPreferencesFlushed, (state, { companyId }) =>
        withEntry(state, companyId, (entry) => ({ ...entry, mycPreferencesDirty: false })),
    ),
);

/** True if any export-modal field is present on the persisted prefs blob. */
function hasExportPrefs(prefs: IMycCompanyPreferences): boolean {
    return (
        prefs.includeBalance !== undefined ||
        prefs.includeIncome !== undefined ||
        prefs.showAccounts !== undefined ||
        prefs.showRatios !== undefined ||
        prefs.selectedNodeIds !== undefined
    );
}

// ─── Selectors ─────────────────────────────────────────────────────────────

export const selectAnalyticsFeature = createFeatureSelector<AnalyticsState>(ANALYTICS_FEATURE_KEY);

export const selectAnalyticsForCompany = (companyId: string) =>
    createSelector(selectAnalyticsFeature, (state) => state.byCompany[companyId] ?? null);
